import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { closeDetails, selectDetailsRollout } from "@dashboard/devices/store";
import { useDeleteRolloutMutation, useUpdateRolloutMutation } from "@dashboard/rollouts/api";
import { ConfirmDialog } from "@devices/components/shared";
import { Typography } from "@mui/material";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { Rollout } from "../../types";

const useStyles = makeStyles()((theme) => ({
    actionContainer: {
        display: "flex",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
    },
}));

type Props = {
    rollout: Rollout;
};

export const RolloutTableActionsCell = ({ rollout }: Props) => {
    const [isCancelling, setIsCancelling] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

    const [updateRollout] = useUpdateRolloutMutation();
    const [deleteRollout] = useDeleteRolloutMutation();

    const dispatch = useDispatch();
    const details = useSelector(selectDetailsRollout);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "modals", "common"]);

    const handleCancel = async () => {
        try {
            setIsCancelling(true);
            await updateRollout({ id: rollout.id, payload: { cancelled: true } }).unwrap();
            showSuccessToast(t("pages:rolloutsPage.toast.cancelRolloutSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleDelete = async () => {
        try {
            setIsDeleting(true);
            await deleteRollout({ id: rollout.id }).unwrap();
            showSuccessToast(t("pages:rolloutsPage.toast.deleteRolloutSuccess"));
            if (details?.selectedId === rollout.id) {
                dispatch(closeDetails());
            }
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setIsDeleting(false);
            setIsDeleteDialogOpen(false);
        }
    };

    return (
        <MinimalTableCell style={{ overflow: "visible" }}>
            <div className={classes.actionContainer}>
                {rollout.status === "active" ? (
                    <DangerOutlinedButton
                        onClick={handleCancel}
                        tooltipProps={{ title: t("pages:rolloutsPage.button.cancelRollout") }}
                        disabled={isCancelling}
                        loading={isCancelling}
                    >
                        <i className="fa-solid fa-ban" />
                    </DangerOutlinedButton>
                ) : null}
                {rollout.status === "cancelled" || rollout.status === "completed" ? (
                    <DangerOutlinedButton
                        onClick={() => setIsDeleteDialogOpen(true)}
                        tooltipProps={{ title: t("pages:rolloutsPage.button.deleteRollout") }}
                        disabled={isDeleting}
                        loading={isDeleting}
                    >
                        <i className="fa-solid fa-trash" />
                    </DangerOutlinedButton>
                ) : null}
            </div>

            <ConfirmDialog
                title={t("modals:deleteRollout.title")}
                open={isDeleteDialogOpen}
                dangerButton
                primaryActionText={t("common:button.delete")}
                onConfirm={handleDelete}
                onClose={() => setIsDeleteDialogOpen(false)}
            >
                <Typography>{t("modals:deleteRollout.description")}</Typography>
            </ConfirmDialog>
        </MinimalTableCell>
    );
};
